import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Briefcase,
  Clock,
  CheckCircle,
  Wallet,
  ArrowUpRight,
  Plus,
  List,
  MessageSquare,
  Receipt,
  Calendar
} from 'lucide-react';

const STATUS_META = {
  NEW: { label: 'Nowa', color: '#3b82f6', badge: 'bg-blue-50 text-blue-700' },
  IN_PROGRESS: { label: 'W realizacji', color: '#f59e0b', badge: 'bg-amber-50 text-amber-700' },
  ASSIGNED: { label: 'Przekazana', color: '#8b5cf6', badge: 'bg-violet-50 text-violet-700' },
  DONE: { label: 'Zaksięgowana', color: '#22c55e', badge: 'bg-emerald-50 text-emerald-700' },
};

const CASE_STATUS_META = {
  NEW: { label: 'Nowe', color: '#3b82f6' },
  IN_PROGRESS: { label: 'W realizacji', color: '#f59e0b' },
  ASSIGNED: { label: 'Przekazane', color: '#8b5cf6' },
  DONE: { label: 'Zakończone', color: '#22c55e' },
};

const formatPLN = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number) + ' PLN';
};

const formatNumber = (value) => new Intl.NumberFormat('pl-PL').format(Number(value || 0));

const Donut = ({ segments, total }) => {
  const size = 168;
  const stroke = 26;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offsetAcc = 0;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={stroke}
        />
        {total > 0 && segments.map((seg) => {
          if (!seg.value) return null;
          const fraction = seg.value / total;
          const dash = fraction * circumference;
          const circleEl = (
            <circle
              key={seg.key}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offsetAcc}
              strokeLinecap="butt"
            />
          );
          offsetAcc += dash;
          return circleEl;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-gray-900">{formatNumber(total)}</span>
        <span className="text-xs text-gray-500">faktur</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, subColor }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
    </div>
    <p className="mt-4 text-sm text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className={`mt-1 text-xs ${subColor || 'text-gray-400'}`}>{sub}</p>}
  </div>
);

const ModernDashboard = ({ data }) => {
  const invoiceCounts = data?.invoiceCounts || {};
  const caseCounts = data?.caseCounts || {};
  const recentInvoices = useMemo(() => (data?.recentInvoices || []).slice(0, 6), [data]);
  const recentCases = useMemo(() => (data?.recentCases || []).slice(0, 6), [data]);

  const invNew = Number(invoiceCounts.NEW || 0);
  const invInProgress = Number(invoiceCounts.IN_PROGRESS || 0);
  const invAssigned = Number(invoiceCounts.ASSIGNED || 0);
  const invDone = Number(invoiceCounts.DONE || 0);
  const invTotal = invNew + invInProgress + invAssigned + invDone;

  const donutSegments = [
    { key: 'NEW', value: invNew, color: STATUS_META.NEW.color },
    { key: 'IN_PROGRESS', value: invInProgress, color: STATUS_META.IN_PROGRESS.color },
    { key: 'ASSIGNED', value: invAssigned, color: STATUS_META.ASSIGNED.color },
    { key: 'DONE', value: invDone, color: STATUS_META.DONE.color },
  ];

  const recentGrossTotal = useMemo(
    () => (data?.recentInvoices || []).reduce((sum, inv) => sum + Number(inv.amountGross || 0), 0),
    [data]
  );

  const topSuppliers = useMemo(() => {
    const map = new Map();
    (data?.recentInvoices || []).forEach((inv) => {
      const name = inv.supplierName || 'Nieznany dostawca';
      map.set(name, (map.get(name) || 0) + Number(inv.amountGross || 0));
    });
    return Array.from(map.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [data]);
  const topSupplierMax = topSuppliers.length ? topSuppliers[0].amount : 0;

  const caseRows = ['NEW', 'IN_PROGRESS', 'ASSIGNED', 'DONE'].map((key) => ({
    key,
    label: CASE_STATUS_META[key].label,
    color: CASE_STATUS_META[key].color,
    value: Number(caseCounts[key] || 0),
  }));
  const caseMax = Math.max(1, ...caseRows.map((r) => r.value));

  const quickActions = [
    { label: 'Wystaw fakturę', to: '/app/invoices/add', icon: Plus, color: 'text-primary-600' },
    { label: 'Lista faktur', to: '/app/invoices/list', icon: List, color: 'text-blue-600' },
    { label: 'Zgłoszenia', to: '/app/tickets', icon: MessageSquare, color: 'text-violet-600' },
    { label: 'Faktury w realizacji', to: '/app/invoices/status/in-progress', icon: Receipt, color: 'text-amber-600' },
  ];

  const today = new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-5">
      {/* Nagłówek */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500">Przegląd systemu CRM Łapy</p>
        </div>
        <div className="flex items-center space-x-2 px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">{today}</span>
        </div>
      </div>

      {/* Karty statystyk */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Faktury ogółem"
          value={formatNumber(invTotal)}
          sub="Wszystkie statusy"
        />
        <StatCard
          icon={Wallet}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Wartość brutto (ostatnie)"
          value={formatPLN(recentGrossTotal)}
          sub={`Z ${formatNumber((data?.recentInvoices || []).length)} ostatnich faktur`}
          subColor="text-emerald-600"
        />
        <StatCard
          icon={Clock}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="W realizacji"
          value={formatNumber(invInProgress)}
          sub="Faktury w toku"
        />
        <StatCard
          icon={CheckCircle}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Zaksięgowane"
          value={formatNumber(invDone)}
          sub="Zakończone faktury"
        />
      </div>

      {/* Wiersz 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Faktury wg statusu */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">Faktury wg statusu</h3>
          <div className="mt-4 flex flex-col items-center">
            <Donut segments={donutSegments} total={invTotal} />
            <div className="mt-5 w-full space-y-2">
              {donutSegments.map((seg) => (
                <div key={seg.key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                    <span className="text-gray-600">{STATUS_META[seg.key].label}</span>
                  </div>
                  <span className="font-semibold text-gray-900">{formatNumber(seg.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Najwięksi dostawcy */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">Najwięksi dostawcy</h3>
          <p className="text-xs text-gray-400 mb-4">Na podstawie ostatnich faktur</p>
          {topSuppliers.length > 0 ? (
            <div className="space-y-4">
              {topSuppliers.map((sup) => (
                <div key={sup.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 truncate pr-2">{sup.name}</span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{formatPLN(sup.amount)}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${topSupplierMax ? Math.max(6, (sup.amount / topSupplierMax) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Brak danych</p>
          )}
        </div>

        {/* Ostatnie faktury */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Ostatnie faktury</h3>
            <Link to="/app/invoices/list" className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center">
              Zobacz wszystkie <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
            </Link>
          </div>
          {recentInvoices.length > 0 ? (
            <div className="mt-4 space-y-3">
              {recentInvoices.map((invoice) => {
                const meta = STATUS_META[invoice.status] || { label: invoice.status, badge: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={invoice.id} className="flex items-center justify-between">
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{invoice.number || '—'}</p>
                      <p className="text-xs text-gray-500 truncate">{invoice.supplierName || '—'}</p>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-sm font-semibold text-gray-900">{formatPLN(invoice.amountGross)}</p>
                      <span className={`inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-500">Brak faktur</p>
          )}
        </div>
      </div>

      {/* Wiersz 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sprawy wg statusu */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">Sprawy wg statusu</h3>
          <div className="mt-4 space-y-4">
            {caseRows.map((row) => (
              <div key={row.key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{row.label}</span>
                  <span className="font-semibold text-gray-900">{formatNumber(row.value)}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${(row.value / caseMax) * 100}%`, backgroundColor: row.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ostatnie sprawy */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">Ostatnie sprawy</h3>
          {recentCases.length > 0 ? (
            <div className="mt-4 space-y-3">
              {recentCases.map((case_) => {
                const meta = CASE_STATUS_META[case_.status] || { label: case_.status, color: '#94a3b8' };
                return (
                  <div key={case_.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 min-w-0 pr-2">
                      <Briefcase className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      <p className="text-sm text-gray-700 truncate">{case_.title || '—'}</p>
                    </div>
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                      style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-500">Brak spraw</p>
          )}
        </div>

        {/* Szybkie akcje */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-900">Szybkie akcje</h3>
          <div className="mt-4 space-y-2">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.to}
                  to={action.to}
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  <span className="h-9 w-9 rounded-lg bg-gray-50 group-hover:bg-white border border-gray-100 flex items-center justify-center">
                    <Icon className={`h-4 w-4 ${action.color}`} />
                  </span>
                  <span className="text-sm font-medium text-gray-700">{action.label}</span>
                  <ArrowUpRight className="h-4 w-4 text-gray-300 ml-auto" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDashboard;
